import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const TOPICS = [
  { icon: '🛫', title: 'New Booking', desc: 'Search and book flights across 500+ airlines.', to: '/newbooking' },
  { icon: '🧾', title: 'Manage My Booking', desc: 'View, download or manage an existing booking.', to: '/my-bookings' },
  { icon: '🔄', title: 'Flight Changes', desc: 'Change your date, time or route after booking.', to: '/flightchanges' },
  { icon: '↩️', title: 'Refund & Cancellation', desc: 'Understand our refund and cancellation policy.', to: '/refund-policy' },
  { icon: '💳', title: 'Service Fees', desc: 'See our transparent fee schedule upfront.', to: '/service-fees' },
  { icon: '✈️', title: 'Airlines We Work With', desc: 'Browse partner airlines and their policies.', to: '/airlines' },
  { icon: '🌍', title: 'Popular Destinations', desc: 'Explore trending routes and destinations.', to: '/destinations' },
  { icon: '💬', title: 'Contact Support', desc: 'Reach our 24/7 team for anything else.', to: '/contact' },
];

const FAQS = [
  { q: 'How do I book a flight?', a: 'Use the search form on our homepage to enter your route and dates, choose from the available fares, and complete checkout. Your e-ticket and confirmation are emailed immediately after payment.' },
  { q: 'How do I view or manage an existing booking?', a: 'Go to "My Bookings", enter the email and booking reference used at checkout, and you\'ll be able to view your itinerary, download documents, or request changes.' },
  { q: 'Can I change my flight after booking?', a: 'Yes. Visit "Flight Changes", enter your booking details, and select a new date, time or route. Any fare difference plus applicable fees will be shown before you confirm.' },
  { q: 'What is your cancellation and refund policy?', a: 'Refund eligibility depends on your fare type. Refundable fares can be cancelled for a refund minus our processing fee; non-refundable fares are subject to airline rules. See our Refund & Cancellation Policy for full details.' },
  { q: 'Are there any hidden fees?', a: 'No. All booking, change and cancellation fees are listed upfront on our Service Fees page and shown again at checkout before you pay.' },
  { q: 'Is my payment information secure?', a: 'Yes. All payments are processed over 256-bit SSL encryption and we never store your full card details on our servers.' },
  { q: 'How soon will I receive my e-ticket?', a: 'Your booking confirmation and PNR are sent to your email immediately after your payment is successfully processed.' },
  { q: 'Can I book for multiple passengers at once?', a: 'Yes — select up to 6 passengers during your search and enter details for each traveler during checkout.' },
  { q: 'What if I need help outside these topics?', a: 'Our support team is available 24/7 by phone or email — reach out any time from the Contact Support page.' },
];

const helpJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(faq => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

export default function HelpPage() {
  return (
    <div>
      <SEO
        title="Help Center"
        canonical="/help"
        description="Flyairlinebooking Help Center — find answers on booking, managing your trip, flight changes, refunds, fees and more, or reach our 24/7 support team."
        keywords="flyairlinebooking help, help center, flight booking support, faq, manage booking, refund policy"
        jsonLd={helpJsonLd}
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-950 text-white py-14 sm:py-20 px-4 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4">Help Center</h1>
        <p className="text-navy-100 text-lg max-w-xl mx-auto">
          Find answers about booking, managing your trip, and getting support — or talk to our team any time.
        </p>
        <a
          href="tel:+18558958503"
          className="inline-flex items-center gap-2 mt-6 bg-amber-500 hover:bg-amber-400 text-navy-950 font-bold px-6 py-3 rounded-xl text-sm transition-colors"
        >
          📞 Call 24/7: +1 (855) 895-8503
        </a>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10 sm:py-16 space-y-14">

        {/* Topic grid */}
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-6">Browse by Topic</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TOPICS.map(topic => (
              <Link
                key={topic.title}
                to={topic.to}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md hover:border-navy-200 transition-all"
              >
                <div className="text-3xl mb-3">{topic.icon}</div>
                <h3 className="font-bold text-gray-900 mb-1 text-sm">{topic.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{topic.desc}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-3 max-w-3xl">
            {FAQS.map(faq => (
              <details key={faq.q} className="bg-gray-50 rounded-xl border border-gray-100 group">
                <summary className="px-4 py-3.5 text-sm font-semibold text-gray-800 cursor-pointer list-none flex justify-between items-center gap-3">
                  {faq.q}
                  <span className="text-gray-400 group-open:rotate-180 transition-transform shrink-0">▼</span>
                </summary>
                <p className="px-4 pb-4 text-sm text-gray-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="bg-gradient-to-br from-navy-50 to-navy-100 rounded-2xl border border-navy-200 p-6 sm:p-8 text-center">
          <p className="text-gray-900 font-bold text-lg mb-1">Still need help?</p>
          <p className="text-gray-600 text-sm mb-5">Our support team is available 24/7, including holidays.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="tel:+18558958503"
              className="inline-block bg-navy-900 hover:bg-navy-800 text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-colors"
            >
              📞 Call +1 (855) 895-8503
            </a>
            <Link
              to="/contact"
              className="inline-block bg-white border border-navy-200 text-navy-900 font-semibold px-6 py-2.5 rounded-xl text-sm hover:bg-navy-50 transition-colors"
            >
              Contact Support →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
